import { useState, useEffect } from 'react';

/**
 * Hook to detect when the component has hydrated on the client side.
 * This helps prevent hydration mismatches between server and client rendering.
 * 
 * @returns boolean - true when hydrated, false during SSR
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const isHydrated = useHydration();
 *   
 *   if (!isHydrated) {
 *     return <div>Loading...</div>; // SSR fallback
 *   }
 *   
 *   return <div>{window.location.href}</div>; // Client-only content
 * }
 * ```
 */
export function useHydration(): boolean {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  return isHydrated;
}

/**
 * Hook that provides a safe way to access browser APIs after hydration.
 * Returns undefined during SSR and the actual value after hydration.
 * 
 * @param getValue - Function that returns the value to access
 * @param fallback - Fallback value to use during SSR
 * @returns The value or fallback
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const userAgent = useClientValue(() => navigator.userAgent, '');
 *   const screenWidth = useClientValue(() => window.innerWidth, 1024);
 *   
 *   return <div>Screen width: {screenWidth}</div>;
 * }
 * ```
 */
export function useClientValue<T>(
  getValue: () => T,
  fallback: T
): T {
  const [value, setValue] = useState<T>(fallback);
  const isHydrated = useHydration();

  useEffect(() => {
    if (isHydrated) {
      try {
        setValue(getValue());
      } catch (error) {
        console.warn('Error accessing client value:', error);
        setValue(fallback);
      }
    }
    // Only run when hydration status changes - functions are stable references
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHydrated]);

  return value;
}

/**
 * Hook for safely accessing localStorage after hydration.
 * 
 * @param key - localStorage key
 * @param fallback - Fallback value if key doesn't exist or during SSR
 * @returns The stored value or fallback
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const theme = useLocalStorage('theme', 'light');
 *   
 *   return <div>Current theme: {theme}</div>;
 * }
 * ```
 */
export function useLocalStorage<T>(
  key: string,
  fallback: T
): T {
  return useClientValue(() => {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : fallback;
  }, fallback);
}

/**
 * Hook for safely accessing sessionStorage after hydration.
 * 
 * @param key - sessionStorage key
 * @param fallback - Fallback value if key doesn't exist or during SSR
 * @returns The stored value or fallback
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const sessionId = useSessionStorage('sessionId', '');
 *   
 *   return <div>Session: {sessionId}</div>;
 * }
 * ```
 */
export function useSessionStorage<T>(
  key: string,
  fallback: T
): T {
  return useClientValue(() => {
    const item = sessionStorage.getItem(key);
    return item ? JSON.parse(item) : fallback;
  }, fallback);
}
